import { countriesType, paths } from "lib/types";

import { RouteKey, localizedPathname } from "./pathnames";

export const privacyAvailableLocales: countriesType[] = ["sv", "en"];

// Static routes that exist in the routing table but have no content in a
// given language. Shared by app/sitemap.ts and app/llms.txt/route.ts so the
// two can never advertise different sets of pages.
export const localeRouteExclusions: { [locale: string]: (keyof paths)[] } = {
  en: ["ecommerce", "newsletter", "case", "campaigns", "research"],
  sv: ["research"],
  da: ["ecommerce", "newsletter", "case", "campaigns", "research"],
  no: ["campaigns"],
};

// Whether a static route has content in the given language, taking both the
// exclusion list and the privacy-policy special case into account.
export const isRouteAvailable = (
  route: keyof paths,
  locale: countriesType,
): boolean => {
  if (route === "privacy") return privacyAvailableLocales.includes(locale);
  return !(localeRouteExclusions[locale] ?? []).includes(route);
};

const routeKeyByPathKey: Record<keyof paths, RouteKey> = {
  home: "/",
  about: "/about",
  contact: "/contact",
  services: "/services",
  posts: "/posts",
  confirmation: "/confirmation",
  newsletter: "/newsletter",
  privacy: "/privacy",
  case: "/case",
  certificate: "/certificate/[slug]",
  ecommerce: "/ecommerce",
  campaigns: "/campaigns",
  research: "/research",
};

// Path translations derived from the routing table in i18n/pathnames.ts,
// which is the single source of truth for localized slugs
export const localizedPaths = (language: countriesType): paths =>
  Object.fromEntries(
    Object.entries(routeKeyByPathKey).map(([pathKey, routeKey]) => [
      pathKey,
      localizedPathname(routeKey, language)
        .replace(/\/\[[^\]]+\]/g, "")
        .replace(/^\//, ""),
    ]),
  ) as unknown as paths;

// Function to get the correct URLs fron env variables
export const canonicalBaseLanguageURLs = (language: countriesType) =>
  ({
    sv: process.env.USEIT_SE_URL,
    no: process.env.USEIT_NO_URL,
    en: process.env.USEIT_EN_URL,
    da: process.env.USEIT_DK_URL,
  })[language];

export const relativeBaseLanguageURLs = (language: countriesType) => {
  const defaultLocale = process.env.DEFAULT_LOCALE?.split(",")[0];
  return language === defaultLocale ? "/" : `/${language}/`;
};

interface MetadataOptions {
  languages?: countriesType[];
  slug?: string;
}

export const getMetadataLanguageAlternatives = (
  route: keyof paths,
  { languages, slug }: MetadataOptions = {},
): Record<string, string> => {
  const availableLanguages = languages || process.env.LOCALES?.split(",") || [];
  const validLocales: countriesType[] = ["sv", "no", "da", "en"];
  return availableLanguages
    .filter((country): country is countriesType =>
      validLocales.includes(country as countriesType),
    )
    .reduce(
      (acc, country) => {
        const paths = localizedPaths(country);
        if (paths && paths[route] !== undefined) {
          acc[country.toLowerCase()] =
            `${canonicalBaseLanguageURLs(country)}${paths[route]}${
              slug ? `/${slug}/` : ""
            }`;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
};

// Resolves the og/social image for a document projected with the shared
// socialImage projection: a custom uploaded image wins, then the editor-
// selected default image (a filename in public/socialmedia/), then the
// site-wide default. The projection always returns the wrapper objects
// (with null members when unset), so the inner url/filename is what must
// be checked - the wrapper objects themselves are always truthy.
export const getArticleImage = (
  data?: {
    socialImage?: {
      customSocialImage?: {
        image?: { url?: string | null } | null;
      } | null;
      defaultSocialImage?: {
        image?: { image?: string | null } | string | null;
      } | null;
    } | null;
  } | null,
): string => {
  const customUrl = data?.socialImage?.customSocialImage?.image?.url;
  if (customUrl) return customUrl;
  const defaultImage = data?.socialImage?.defaultSocialImage?.image;
  const defaultFileName =
    typeof defaultImage === "string" ? defaultImage : defaultImage?.image;
  if (defaultFileName) return `socialmedia/${defaultFileName}`;
  return "default_og_image.png";
};

export const getCompanyName = (locale: countriesType) =>
  ({
    en: "Useit",
    sv: "Useit Sverige",
    no: "Useit Norge",
    da: "Useit Danmark",
  })[locale || "sv"];
